<template>
  <div class="product-custom-select" :class="blockClass">
    <button
      type="button"
      class="product-custom-select__trigger text-mobile-paragraph-m-bold text-desktop-paragraph-m-bold"
      :aria-haspopup="!loading ? 'listbox' : undefined"
      :aria-expanded="!loading ? isOpen : undefined"
      :aria-controls="!loading ? `dropdown-${uniqueId}` : undefined"
      :disabled="loading"
      @click.prevent.stop="!loading && toggleDropdown()"
      @keydown="!loading && handleTriggerKeydown($event)"
    >
      <div
        v-if="
          !loading &&
          !hideFlavorImage &&
          !hideFlavorImageOnButton &&
          selectedOption?.flavorImage &&
          (selectedOption.flavorImage.src || selectedOption.flavorImage.url)
        "
        class="product-custom-select__image-wrapper"
      >
        <responsive-image
          class="product-custom-select__image"
          :image="selectedOption.flavorImage"
          ratio="1:1"
        />
      </div>
      <div v-if="!loading" class="product-custom-select__content">
        <div class="product-custom-select__title-row">
          <span
            class="product-custom-select__title text-mobile-paragraph-m-semibold text-desktop-paragraph-s-semibold"
            v-text="selectedLabel || placeholder"
          />
          <span
            v-if="
              selectedOption?.flavorBadge &&
              !selectedOption?.disabled &&
              !hideFlavorBadge &&
              !hideFlavorBadgeOnButton
            "
            class="text-mobile-paragraph-xs-bold"
            :class="[
              blockClass
                ? `${blockClass}__badge`
                : 'product-custom-select__badge',
              selectedOption.flavorBadge === newBadge
                ? blockClass
                  ? `${blockClass}__badge--black`
                  : 'product-custom-select__badge--black'
                : '',
            ]"
            v-text="selectedOption.flavorBadge"
          />
          <span
            v-if="selectedOption?.disabled"
            class="text-mobile-paragraph-xs-bold"
            :class="[
              blockClass
                ? `${blockClass}__badge`
                : 'product-custom-select__badge',
              blockClass
                ? `${blockClass}__badge--soldout`
                : 'product-custom-select__badge--soldout',
            ]"
            v-text="disabledGroupLabel"
          />
        </div>

        <div
          v-if="countLabel"
          class="product-custom-select__option-count text-mobile-paragraph-xs-regular text-desktop-paragraph-xs-regular"
          v-html="countLabel"
        />
      </div>
    </button>

    <!-- Mobile overlay -->
    <teleport to="body">
      <overlay
        v-if="!loading && isMobile && !forceDesktopDropdown"
        direction="up"
        block-class="product-custom-select__overlay"
        type="shelf"
        :namespace="modalNamespace"
        shelf-close
      >
        <template #body>
          <ul
            js-body-scroll-lock-ignore
            class="product-custom-select__list"
            :aria-label="ariaLabel"
          >
            <template v-for="option of availableOptions" :key="option.key">
              <li
                class="product-custom-select__option"
                :class="{
                  'is-current': selectVariant === option.value,
                  'has-flavor-image':
                    !hideFlavorImage &&
                    option.flavorImage &&
                    (option.flavorImage.src || option.flavorImage.url),
                }"
              >
                <button
                  class="product-custom-select__button"
                  type="button"
                  @click="handleOptionClick(option.value)"
                >
                  <div
                    v-if="
                      !hideFlavorImage &&
                      option.flavorImage &&
                      (option.flavorImage.src || option.flavorImage.url)
                    "
                    class="product-custom-select__image-wrapper"
                  >
                    <responsive-image
                      class="product-custom-select__image"
                      :image="option.flavorImage"
                      ratio="1:1"
                    />
                  </div>
                  <div class="product-custom-select__option-content">
                    <p
                      class="text-mobile-paragraph-m-bold text-desktop-paragraph-m-bold"
                      v-text="option.label"
                    />
                    <span
                      v-if="option.flavorBadge && !hideFlavorBadge"
                      class="text-mobile-paragraph-xs-bold"
                      :class="[
                        blockClass
                          ? `${blockClass}__badge`
                          : 'product-custom-select__badge',
                        option.flavorBadge === newBadge
                          ? blockClass
                            ? `${blockClass}__badge--black`
                            : 'product-custom-select__badge--black'
                          : '',
                      ]"
                      v-text="option.flavorBadge"
                    />
                  </div>
                </button>
                <icon-check-outline
                  v-if="selectVariant === option.value"
                  class="product-custom-select__check-icon"
                />
              </li>
            </template>

            <template v-if="disabledOptions.length > 0">
              <li
                v-for="option of disabledOptions"
                :key="option.key"
                class="product-custom-select__option is-soldout"
                :class="{
                  'has-flavor-image':
                    !hideFlavorImage &&
                    option.flavorImage &&
                    (option.flavorImage.src || option.flavorImage.url),
                }"
              >
                <button
                  class="product-custom-select__button"
                  type="button"
                  @click="handleOptionClick(option.value)"
                >
                  <div
                    v-if="
                      !hideFlavorImage &&
                      option.flavorImage &&
                      (option.flavorImage.src || option.flavorImage.url)
                    "
                    class="product-custom-select__image-wrapper"
                  >
                    <responsive-image
                      class="product-custom-select__image"
                      :image="option.flavorImage"
                      ratio="1:1"
                    />
                  </div>
                  <div class="product-custom-select__option-content">
                    <p
                      class="text-mobile-paragraph-m-semibold text-desktop-paragraph-m-semibold"
                      v-text="option.label"
                    />
                    <span
                      class="text-mobile-paragraph-s-bold text-desktop-paragraph-s-bold"
                      :class="[
                        blockClass
                          ? `${blockClass}__badge`
                          : 'product-custom-select__badge',
                        blockClass
                          ? `${blockClass}__badge--soldout`
                          : 'product-custom-select__badge--grey',
                      ]"
                      v-text="disabledGroupLabel"
                    />
                  </div>
                </button>
              </li>
            </template>
          </ul>
        </template>
      </overlay>
    </teleport>

    <!-- Desktop dropdown -->
    <div
      v-if="!loading && (!isMobile || forceDesktopDropdown)"
      v-show="isOpen"
      :id="`dropdown-${uniqueId}`"
      class="product-custom-select__dropdown"
      :class="{ 'product-custom-select__dropdown--full-size': styleFullSize }"
    >
      <ul
        role="listbox"
        :aria-label="ariaLabel"
        class="product-custom-select__list"
        :aria-activedescendant="activeDescendant"
        tabindex="0"
        js-body-scroll-lock-ignore
        @keydown="handleListKeydown"
      >
        <template v-for="option of availableOptions" :key="option.key">
          <li
            :id="`option-${option.key}`"
            role="option"
            :aria-selected="selectVariant === option.value ? 'true' : 'false'"
            aria-disabled="false"
            class="product-custom-select__option"
            :class="{
              'is-current': selectVariant === option.value,
              'is-focused': activeDescendant === `option-${option.key}`,
              'has-flavor-image':
                !hideFlavorImage &&
                option.flavorImage &&
                (option.flavorImage.src || option.flavorImage.url),
            }"
          >
            <button
              class="product-custom-select__button"
              type="button"
              tabindex="-1"
              @click="handleOptionClick(option.value)"
            >
              <div
                v-if="
                  !hideFlavorImage &&
                  option.flavorImage &&
                  (option.flavorImage.src || option.flavorImage.url)
                "
                class="product-custom-select__image-wrapper"
              >
                <responsive-image
                  class="product-custom-select__image"
                  :image="option.flavorImage"
                  ratio="1:1"
                  sizes="(min-width: 1024px) 620px, 375px"
                />
              </div>
              <div class="product-custom-select__option-content">
                <p
                  class="text-mobile-paragraph-m-bold text-desktop-paragraph-s-bold"
                  v-text="option.label"
                />
                <span
                  v-if="option.flavorBadge && !hideFlavorBadge"
                  class="text-mobile-paragraph-xs-bold"
                  :class="[
                    blockClass
                      ? `${blockClass}__badge`
                      : 'product-custom-select__badge',
                    option.flavorBadge === newBadge
                      ? blockClass
                        ? `${blockClass}__badge--black`
                        : 'product-custom-select__badge--black'
                      : '',
                  ]"
                  v-text="option.flavorBadge"
                />
              </div>
            </button>
            <icon-check-outline
              v-if="selectVariant === option.value"
              class="product-custom-select__check-icon"
              aria-hidden="true"
            />
          </li>
        </template>

        <template v-if="disabledOptions.length > 0">
          <li
            v-for="option of disabledOptions"
            :key="option.key"
            role="option"
            aria-selected="false"
            aria-disabled="true"
            class="product-custom-select__option is-soldout"
            :class="{
              'has-flavor-image':
                !hideFlavorImage &&
                option.flavorImage &&
                (option.flavorImage.src || option.flavorImage.url),
            }"
          >
            <button
              class="product-custom-select__button"
              type="button"
              tabindex="-1"
              @click="handleOptionClick(option.value, true)"
            >
              <div
                v-if="
                  !hideFlavorImage &&
                  option.flavorImage &&
                  (option.flavorImage.src || option.flavorImage.url)
                "
                class="product-custom-select__image-wrapper"
              >
                <responsive-image
                  class="product-custom-select__image"
                  :image="option.flavorImage"
                  ratio="1:1"
                  sizes="(min-width: 1024px) 620px, 375px"
                />
              </div>
              <div class="product-custom-select__option-content">
                <p
                  class="text-mobile-paragraph-m-bold text-desktop-paragraph-s-bold"
                  v-text="option.label"
                />
                <span
                  class="text-mobile-paragraph-xs-bold"
                  :class="[
                    blockClass
                      ? `${blockClass}__badge`
                      : 'product-custom-select__badge',
                    blockClass
                      ? `${blockClass}__badge--soldout`
                      : 'product-custom-select__badge--grey',
                  ]"
                  v-text="disabledGroupLabel"
                />
              </div>
            </button>
          </li>
        </template>
      </ul>
    </div>

    <div
      v-if="!loading"
      class="product-custom-select__trigger-icon"
      aria-hidden="true"
      @click.prevent.stop="toggleDropdown"
    >
      <icon-chevron-up v-if="isOpen" />
      <icon-chevron-down v-else />
    </div>
  </div>
</template>

<script>
/**
 * Vue: Product Custom Select
 * -----------------------------------------------------------------------------
 * Reusable custom select dropdown component.
 *
 * @param {String|Number} selectVariant - Currently selected value.
 * @param {Array} options - Array of option objects with { key, label, value, disabled, flavorImage, flavorBadge }.
 * @param {String} [ariaLabel] - Accessible label for the select.
 * @param {String} [blockClass] - Additional CSS class for styling variations.
 * @param {String} [countLabel] - Optional count label to display.
 * @param {String} [disabledGroupLabel] - Label for disabled options group.
 * @param {Boolean} [forceDesktopDropdown] - Force desktop dropdown on mobile.
 * @param {Boolean} [hideFlavorImage] - Hide flavor images in all contexts.
 * @param {Boolean} [hideFlavorImageOnButton] - Hide flavor image in trigger button only.
 * @param {Boolean} [hideFlavorBadge] - Hide flavor badges in all contexts.
 * @param {Boolean} [hideFlavorBadgeOnButton] - Hide flavor badge in trigger button only.
 * @param {Boolean} [loading] - Loading state.
 * @param {String} [placeholder] - Placeholder text when no value selected.
 * @param {String} [uniqueId] - Unique identifier for ARIA attributes and DOM elements.
 * @param {Boolean} [styleFullSize] - Apply full-size style to dropdown matching parent container width/height.
 *
 * @emits update:selectVariant - Emit new selected value.
 * @emits dropdown-toggled - Emit isOpen value (dropdown was opened/closed).
 * @emits variant-selector-namespace - Emit overlay namespace
 */
import { mapActions, mapState } from 'pinia';
import useOverlaysStore from '~stores/overlays';
import useIndexStore from '~stores/index';

import IconChevronDown from '~icons/directional-navigation/chevron/down.svg';
import IconChevronUp from '~icons/directional-navigation/chevron/up.svg';
import IconCheckOutline from '~icons/general/check-outline.svg';
import Overlay from '~global/overlay/overlay.vue';
import ResponsiveImage from '~global/images/responsive-image';
export default {
  name: 'ProductCustomSelect',

  components: {
    IconChevronDown,
    IconChevronUp,
    IconCheckOutline,
    Overlay,
    ResponsiveImage,
  },

  props: {
    selectVariant: {
      type: [String, Number],
      default: '',
    },
    options: {
      type: Array,
      required: true,
    },
    ariaLabel: {
      type: String,
      default: '',
    },
    blockClass: {
      type: String,
      default: '',
    },
    countLabel: {
      type: String,
      default: '',
    },
    disabledGroupLabel: {
      type: String,
      default: 'Unavailable',
    },
    forceDesktopDropdown: {
      type: Boolean,
      default: false,
    },
    hideFlavorImage: {
      type: Boolean,
      default: false,
    },
    hideFlavorImageOnButton: {
      type: Boolean,
      default: false,
    },
    hideFlavorBadge: {
      type: Boolean,
      default: false,
    },
    hideFlavorBadgeOnButton: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: 'Select an option',
    },
    // Generates a lightweight random ID for DOM element IDs and ARIA attributes only.
    // Not intended for security purposes - sufficient for preventing UI element ID collisions.
    uniqueId: {
      type: String,
      default: () => `select-${Math.random().toString(36).substring(2, 11)}`,
    },
    styleFullSize: {
      type: Boolean,
      default: false,
    },
  },

  emits: {
    'update:selectVariant': (value) => {
      return typeof value === 'string' || typeof value === 'number';
    },
    'dropdown-toggled': (value) => typeof value === 'boolean',
    'variant-selector-namespace': (value) => typeof value === 'string',
  },

  data() {
    return {
      cnvsGlobal: true,
      isOpen: false,
      activeDescendant: null,
      focusedOptionIndex: -1,
      newBadge: 'NEW',
    };
  },

  computed: {
    ...mapState(useIndexStore, {
      isMobile: (state) => state.screen.mobile,
    }),

    /**
     * Get available (enabled) options.
     * @returns {Array}
     */
    availableOptions() {
      return this.options.filter((option) => !option.disabled);
    },

    /**
     * Get disabled options.
     * @returns {Array}
     */
    disabledOptions() {
      return this.options.filter((option) => option.disabled);
    },

    /**
     * Get label for currently selected value.
     * @returns {String}
     */
    selectedLabel() {
      const selected = this.options.find(
        (option) => option.value === this.selectVariant,
      );
      return selected?.label || '';
    },

    /**
     * Get currently selected option object.
     * @returns {Object|undefined}
     */
    selectedOption() {
      return this.options.find((option) => option.value === this.selectVariant);
    },

    modalNamespace() {
      return `variantSelectorPopup-${this.uniqueId}`;
    },
  },

  mounted() {
    document.addEventListener('click', this.handleClickOutside);
    this.$emit('variant-selector-namespace', this.modalNamespace);
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },

  methods: {
    ...mapActions(useOverlaysStore, {
      closeOverlay: 'close',
      openOverlay: 'open',
    }),

    setOpen(value) {
      this.isOpen = value;
      this.$emit('dropdown-toggled', value);
    },

    /**
     * Toggle dropdown.
     */
    toggleDropdown() {
      // On mobile (unless forced desktop), use overlay
      if (this.isMobile && !this.forceDesktopDropdown) {
        this.handleOpenOverlay();
        return;
      }

      // Desktop dropdown behavior
      this.setOpen(!this.isOpen);

      if (this.isOpen) {
        this.$nextTick().then(() => {
          const currentIndex = this.availableOptions.findIndex(
            (opt) => opt.value === this.selectVariant,
          );
          this.focusedOptionIndex = currentIndex >= 0 ? currentIndex : 0;

          if (this.focusedOptionIndex >= 0) {
            this.activeDescendant = `option-${this.availableOptions[this.focusedOptionIndex].key}`;
          }

          this.scrollToOption();
        });
      } else {
        this.focusedOptionIndex = -1;
        this.activeDescendant = null;
      }
    },

    /**
     * Open mobile overlay.
     */
    handleOpenOverlay() {
      this.openOverlay({
        namespace: this.modalNamespace,
        ignoreDismissed: true,
      });
    },

    /**
     * Handle keyboard navigation on trigger button.
     */
    handleTriggerKeydown(event) {
      const { key } = event;

      if (
        ['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(key) &&
        !this.isOpen
      ) {
        event.preventDefault();
        this.toggleDropdown();
      }
    },

    /**
     * Handle keyboard navigation within listbox.
     */
    handleListKeydown(event) {
      const { key } = event;

      if (!this.availableOptions.length) return;

      switch (key) {
        case 'ArrowDown':
          event.preventDefault();
          this.focusedOptionIndex =
            (this.focusedOptionIndex + 1) % this.availableOptions.length;
          this.activeDescendant = `option-${this.availableOptions[this.focusedOptionIndex].key}`;
          this.scrollToOption();
          break;

        case 'ArrowUp':
          event.preventDefault();
          this.focusedOptionIndex =
            this.focusedOptionIndex <= 0
              ? this.availableOptions.length - 1
              : this.focusedOptionIndex - 1;
          this.activeDescendant = `option-${this.availableOptions[this.focusedOptionIndex].key}`;
          this.scrollToOption();
          break;

        case 'Enter':
        case ' ':
          event.preventDefault();
          if (this.focusedOptionIndex >= 0) {
            this.handleOptionClick(
              this.availableOptions[this.focusedOptionIndex].value,
              true,
            );
          }
          break;

        case 'Escape':
          event.preventDefault();
          this.toggleDropdown();
          break;

        default:
          break;
      }
    },

    /**
     * Scroll focused option into view.
     */
    scrollToOption() {
      this.$nextTick().then(() => {
        const focusedElement = document.getElementById(this.activeDescendant);
        if (focusedElement) {
          focusedElement.scrollIntoView({
            block: 'nearest',
            inline: 'nearest',
          });
        }
      });
    },

    /**
     * Handle option click.
     */
    handleOptionClick(value, shouldReturnFocus = false) {
      this.$emit('update:selectVariant', value);
      this.setOpen(false);
      this.focusedOptionIndex = -1;
      this.activeDescendant = null;

      // Close mobile overlay if applicable
      if (this.isMobile && !this.forceDesktopDropdown) {
        this.closeOverlay({
          namespace: this.modalNamespace,
        });
      }

      if (shouldReturnFocus) {
        this.$nextTick().then(() => {
          const trigger = this.$el?.querySelector(
            '.product-custom-select__trigger',
          );
          if (trigger) {
            trigger.focus();
          }
        });
      }
    },

    /**
     * Handle click outside to close dropdown.
     */
    handleClickOutside(event) {
      if (!this.isOpen) return;

      const openDropdown = this.$el?.querySelector(
        '.product-custom-select__dropdown',
      );
      if (openDropdown && !openDropdown.parentElement.contains(event.target)) {
        this.setOpen(false);
      }
    },
  },
};
</script>

<style lang="scss">
@import './product-custom-select';
</style>
